<template>
  <!-- Main header -->
  <header class="main-header">
    <!-- Main navbar -->
    <nav class="navbar navbar-expand-lg py-lg-0">
      <!-- Logo section - mobile only -->
      <div class="mobile-logo-section border-0 d-lg-none w-100 text-center py-0 d-flex justify-content-center">
        <a class="navbar-brand m-0" href="/">
          <img src="@/assets/zapiola-logo.png" alt="Zapiola" class="logo img-fluid" style="width: 100px; height: auto;">
        </a>
      </div>

      <div class="container px-3">
        <!-- Mobile actions bar -->
        <div class="mobile-actions-bar d-lg-none w-100">
          <button class="navbar-toggler" type="button" @click="toggleMobileMenu">
            <i class="fas fa-bars"></i>
          </button>

          <div class="mobile-right-actions">
            <a href="/iniciar-sesion" class="nav-action-btn mobile">
              <i class="fas fa-user fs-5"></i>
            </a>
            <a href="/carrito" class="nav-action-btn mobile cart-btn">
              <i class="fas fa-shopping-cart fs-5"></i>
              <div v-if="cartCount > 0" class="cart-badge mobile">
                {{ cartCount }}
              </div>
            </a>
          </div>
        </div>

        <!-- Search bar - mobile -->
        <div class="mobile-search-bar d-lg-none w-100">
          <div class="search-container">
            <div class="search-wrapper">
              <i class="fas fa-search search-icon"></i>
              <input
                type="text"
                v-model="searchQuery"
                class="search-input border border-dark border-1"
                placeholder="¿Qué estás buscando?"
                @focus="handleSearchFocus"
                @blur="handleSearchBlur"
                @input="handleSearchInput"
                @keyup.enter="submitSearch"
              >
              <button v-if="searchQuery" @click="clearSearch" class="clear-button">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Search Results Dropdown -->
            <div v-show="showResults && searchQuery.length >= 2" class="search-dropdown">
              <!-- Loading State -->
              <div v-if="isSearching" class="search-loading">
                <div class="loading-item">
                  <div class="loading-image"></div>
                  <div class="loading-content">
                    <div class="loading-line"></div>
                    <div class="loading-line short"></div>
                  </div>
                </div>
              </div>

              <!-- Search Button - Always Show -->
              <div class="no-results-search-button">
                <button @click="submitSearch" class="search-all-btn">
                  <span>Buscar "{{ searchQuery }}"</span>
                  <i class="fas fa-search ms-auto"></i>
                </button>
              </div>

              <!-- Results List - Only Show if Results Found -->
              <div v-if="!isSearching && filteredProducts.length > 0" class="results-list">
                <a
                  v-for="product in displayedProducts"
                  :key="product.id"
                  class="result-item"
                  :class="{ 'highlighted': highlightedIndex === product.id }"
                  @click.prevent="searchAndNavigate(product)"
                  @mouseenter="highlightedIndex = product.id"
                >
                  <img 
                    :src="product.image || require('@/assets/images/zapiola-logo.png')" 
                    :alt="product.name"
                    class="result-image"
                  >
                  <div class="result-details">
                    <h6>{{ product.name }}</h6>
                    <span class="result-category">{{ product.category_name }}</span>
                    <div v-if="product.price > 0" class="result-price">
                      <span v-if="product.discount > 0" class="original-price">
                        ${{ formatPrice(product.price) }}
                      </span>
                      <span class="final-price" :class="{'discount-price': product.discount > 0}">
                        ${{ formatPrice(product.discount > 0 ? product.price * (1 - product.discount/100) : product.price) }}
                      </span>
                      <span v-if="product.discount > 0" class="discount-badge">
                        -{{ product.discount }}%
                      </span>
                    </div>
                  </div>
                </a>

                <!-- Show more results if available -->
                <div v-if="hasMoreResults" class="more-results">
                  <button @click="viewAllResults" class="view-all-btn">
                    Ver todos los resultados ({{ totalResults }})
                    <i class="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop layout -->
        <div class="desktop-content d-none d-lg-flex w-100">
          <button class="navbar-toggler d-lg-none" 
                  type="button" 
                  @click="toggleMobileMenu">
            <i class="fas fa-bars"></i>
          </button>
          <!-- Logo -->
          <a class="navbar-brand" href="/">
            <img 
              src="@/assets/zapiola-logo.png" 
              alt="Zapiola" 
              class="logo"
            />
          </a>

          <div class="search-container me-lg-4">
            <div class="search-wrapper">
              <i class="fas fa-search search-icon"></i>
              <input
                type="text"
                v-model="searchQuery"
                class="search-input border border-dark border-1"
                placeholder="¿Qué estás buscando?"
                @focus="handleSearchFocus"
                @blur="handleSearchBlur"
                @input="handleSearchInput"
                @keyup.enter="submitSearch"
              >
              <button v-if="searchQuery" @click="clearSearch" class="clear-button">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Search Results Dropdown -->
            <div v-show="showResults && searchQuery.length >= 2" class="search-dropdown">
              <!-- Loading State -->
              <div v-if="isSearching" class="search-loading">
                <div class="loading-item">
                  <div class="loading-image"></div>
                  <div class="loading-content">
                    <div class="loading-line"></div>
                    <div class="loading-line short"></div>
                  </div>
                </div>
              </div>

              <!-- Search Button - Always Show -->
              <div class="no-results-search-button">
                <button @click="submitSearch" class="search-all-btn">
                  <span>Buscar "{{ searchQuery }}"</span>
                  <i class="fas fa-search ms-auto"></i>
                </button>
              </div>

              <!-- Results List - Only Show if Results Found -->
              <div v-if="!isSearching && filteredProducts.length > 0" class="results-list">
                <a
                  v-for="product in displayedProducts"
                  :key="product.id"
                  class="result-item"
                  :class="{ 'highlighted': highlightedIndex === product.id }"
                  @click.prevent="searchAndNavigate(product)"
                  @mouseenter="highlightedIndex = product.id"
                >
                  <img 
                    :src="product.image || require('@/assets/images/zapiola-logo.png')" 
                    :alt="product.name"
                    class="result-image"
                  >
                  <div class="result-details">
                    <h6>{{ product.name }}</h6>
                    <span class="result-category">{{ product.category_name }}</span>
                    <div v-if="product.price > 0" class="result-price">
                      <span v-if="product.discount > 0" class="original-price">
                        ${{ formatPrice(product.price) }}
                      </span>
                      <span class="final-price fs-7" :class="{'discount-price': product.discount > 0}">
                        ${{ formatPrice(product.discount > 0 ? product.price * (1 - product.discount/100) : product.price) }}
                      </span>
                      <span v-if="product.discount > 0" class="discount-badge">
                        -{{ product.discount }}%
                      </span>
                    </div>
                  </div>
                </a>

                <!-- Show more results if available -->
                <div v-if="hasMoreResults" class="more-results">
                  <button @click="viewAllResults" class="view-all-btn">
                    Ver todos los resultados ({{ totalResults }})
                    <i class="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Right actions - Desktop -->
          <div class="d-flex align-items-center gap-4 order-lg-3">
            <a href="/iniciar-sesion" class="nav-action-btn">
              <i class="fas fa-user fs-5"></i>
              <span>Mi cuenta</span>
            </a>
            <a href="/carrito" class="nav-action-btn cart-btn">
              <i class="fas fa-shopping-cart fs-5"></i>
              <span>Carrito</span>
              <div v-if="cartCount > 0" class="cart-badge">
                {{ cartCount }}
              </div>
            </a>
          </div>
        </div>

        <!-- Mobile Menu -->
        <div class="mobile-menu" :class="{ 'show': isMobileMenuOpen }">
          <div class="mobile-menu-header">
            <button class="btn-close" @click="toggleMobileMenu"></button>
          </div>
          <div class="mobile-menu-content">
            <ul class="mobile-nav-list">
              <li v-for="item in menuItems" 
                  :key="item.value" 
                  class="mobile-nav-item">
                <a :href="`/productos?categories=${encodeURIComponent(item.value)}`"
                   @click="toggleMobileMenu">
                  {{ item.name }}
                </a>
              </li>
            </ul>
            <div class="mobile-menu-footer">
              <a href="/login" class="btn btn-outline-primary w-100 mb-2">
                <i class="far fa-user me-2"></i>
                Iniciar Sesión
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Categories navbar - visible only on desktop -->
    <nav class="navbar py-0 navbar-expand-lg navbar-dark bg-dark py-1 d-none d-lg-block px-3">
      <div class="container">
        <div class="collapse navbar-collapse d-flex justify-content-around">
          <ul class="navbar-nav d-flex w-100 justify-content-between">
            <li v-for="item in menuItems" 
                :key="item.value" 
                class="nav-item">
              <a class="nav-link" 
                 :href="`/productos?categories=${encodeURIComponent(item.value)}`">
                {{ item.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getProducts } from '@/services/products'
import { formatPrice } from '@/helpers/product'
import { useStore } from 'vuex'

const router = useRouter()
const products = ref([])
const isLoading = ref(true)

// Función para cargar productos
const loadProducts = async () => {
  try {
    isLoading.value = true
    console.log('Loading products...')

    // Use a smaller page size to improve loading speed
    const params = {
      page: 1,
      page_size: 20 // Request fewer products to reduce load time
    }

    // Si no hay caché, cargar de la API
    const response = await getProducts(params)

    // Handle both response formats safely
    if (response && response.results) {
      products.value = response.results
      console.log(`Loaded ${response.results.length} products for search`)
    } else if (Array.isArray(response)) {
      products.value = response
      console.log(`Loaded ${response.length} products for search (array format)`)
    } else {
      console.warn('Unexpected or empty response format:', response)
      products.value = []
    }

    console.log('Products loaded successfully')
  } catch (error) {
    console.error('Error loading products:', error)
    products.value = []
    
    // Show a user-friendly error message
    // toast.error('Error cargando los productos. Por favor, intenta nuevamente más tarde.')
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  console.log('NavbarComponent mounted')
  await loadProducts()
})

// Actualizar la función de filtrado para usar la misma lógica que ProductList
const filteredProducts = computed(() => {
  if (!searchQuery.value.trim() || searchQuery.value.length < 2) {
    return []
  }

  // Asegurar que products.value es un array
  if (!Array.isArray(products.value)) {
    console.error('products.value is not an array:', products.value)
    return []
  }
  
  const search = searchQuery.value.toLowerCase().trim()
  
  console.log(`Filtering ${products.value.length} products with search query: "${search}"`)
  
  try {
    const results = products.value.filter(product => {
      if (!product) return false
      
      if (!product.is_available) return false

      // Usar exactamente la misma lógica que en ProductList
      const searchableText = [
        product.name,
        product.brand,
        product.category_name,
        product.description
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      
      // Para depuración - ver qué productos tienen relación con 'carpa'
      if (search.includes('carpa')) {
        console.log(`Product ${product.id} - ${product.name}: Category: ${product.category_name || 'N/A'}, Contains 'carpa'? ${searchableText.includes('carpa')}`);
      }
      
      return searchableText.includes(search)
    })
    
    console.log(`Found ${results.length} matching products for "${search}"`)
    return results
  } catch (error) {
    console.error('Error filtering products:', error)
    return []
  }
})

const searchQuery = ref('');
const isMobileMenuOpen = ref(false)

// Actualizar menuItems para que coincidan exactamente con category_name
const menuItems = [
  { name: 'Artículos', value: 'Otros' },
  { name: 'Pipas', value: 'Pipas' },
  { name: 'Picadores', value: 'Picadores' },
  { name: 'Sustratos', value: 'Sustratos' },
  { name: 'Hidroponia', value: 'Hidroponia' },
  { name: 'Fertilizantes', value: 'Fertilizantes' },
  { name: 'Control de plagas', value: 'Plaguicidas' },
  { name: 'Artículos para indoor', value: 'Artículos indoor' },
  { name: 'Indumentaria', value: 'Indumentaria' },
  { name: 'Todos', value: '' },
];

// Modificar/agregar estas funciones de navegación
const searchAndNavigate = (product) => {
  router.push({
    path: `/producto/${product.id}`,
  });
  clearSearch();
};

// Función para ver todos los resultados
const viewAllResults = () => {
  router.push({
    path: '/productos',
    query: { search: searchQuery.value }
  });
  clearSearch();
};

// Remover la referencia a navigateToSearch ya que no es necesaria

// Estado para búsqueda
const showResults = ref(false);
const isSearching = ref(false);
const highlightedIndex = ref(null);
const searchDebounceTimeout = ref(null);
const totalResults = ref(0);

// Computed properties para resultados de búsqueda
const displayedProducts = computed(() => filteredProducts.value.slice(0, 4));
const hasMoreResults = computed(() => totalResults.value > 4);

const handleSearchFocus = () => {
  showResults.value = true;
  handleSearchInput();
};

const handleSearchInput = () => {
  clearTimeout(searchDebounceTimeout.value)
  
  if (searchQuery.value.length >= 2) {
    isSearching.value = true
    showResults.value = true
    
    searchDebounceTimeout.value = setTimeout(async () => {
      // Si no hay productos en memoria, intentar cargar del caché o API
      if (products.value.length === 0) {
        console.log('No products in memory, loading from API')
        await loadProducts()
      }
      
      totalResults.value = filteredProducts.value.length
      isSearching.value = false
      console.log(`Search completed: ${totalResults.value} results found`)
    }, 300)
  } else {
    showResults.value = false
  }
}

const clearSearch = () => {
  searchQuery.value = '';
  showResults.value = false;
}

const handleSearchBlur = () => {
  // Pequeño delay para permitir clicks en resultados
  setTimeout(() => {
    showResults.value = false
  }, 200)
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
}

// Add store and computed for cart count
const store = useStore()
const cartCount = computed(() => store.getters.cartCount)

// Add submitSearch function to handle Enter key presses
const submitSearch = () => {
  // Only submit if the search query has content
  if (searchQuery.value.trim().length >= 2) {
    router.push({
      path: '/productos',
      query: { search: searchQuery.value.trim() }
    });
    // Close the results dropdown and any mobile UI elements
    showResults.value = false;
    if (isMobileMenuOpen.value) {
      toggleMobileMenu();
    }
  }
};
  
</script>

<style scoped>
.logo {
  height: 70px;
  width: auto;
}

.navbar {
  background-color: var(--white-color);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.navbar-dark {
  background-color: var(--black-color-3);
}

.navbar-dark .nav-link {
  color: var(--white-color-2) !important;
  transition: color 0.2s;
  font-weight: 500;
}

.navbar-dark .nav-link:hover {
  color: var(--white-color) !important;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--black-color);
}

.btn-primary:hover {
  background-color: var(--dark-primary-color);
  border-color: var(--dark-primary-color);
}

@media (max-width: 991.98px) {
  .search-container {
    order: 1;
    margin: 0.5rem 0;
    max-width: 100%;
  }

  .navbar > .container {
    flex-wrap: wrap;
  }

  .navbar-toggler {
    padding: 0.5rem;
    font-size: 1.25rem;
    border: none;
    color: #212529;
  }

  .search-input {
    height: 40px;
    font-size: 16px; /* Prevents zoom on iOS */
  }

  .btn-primary {
    padding: 0.5rem;
  }
}

/* Mobile Menu Styles */
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: white;
  z-index: 1600; /* Ajustado para estar por debajo del filtro móvil pero por encima de la navbar */
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;
  overflow-y: auto;
}

.mobile-menu.show {
  transform: translateX(0);
}

.mobile-menu-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
}

.mobile-menu-content {
  padding: 1rem;
}

.mobile-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.mobile-nav-item {
  border-bottom: 1px solid #dee2e6;
}

.mobile-nav-item > a,
.mobile-nav-toggle {
  padding: 1rem;
  display: block;
  color: #212529;
  text-decoration: none;
  font-size: 1rem;
}

.mobile-nav-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.mobile-submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #f8f9fa;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.mobile-submenu.show {
  max-height: 500px;
}

.mobile-submenu a {
  padding: 0.75rem 1.5rem;
  display: block;
  color: #6c757d;
  text-decoration: none;
  font-size: 0.9rem;
}

.mobile-menu-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  margin-top: 1rem;
}

.main-header {
  position: relative;
  z-index: 999; /* Ajustado para estar por debajo del filtro móvil pero por encima de otros elementos */
}

/* Search Styles */
.search-container {
  position: relative;
  width: 100%;
  max-width: 600px;
}

.search-form {
  display: flex;
  align-items: center;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem;
  border: none;
  border-radius: 12px;
  background: var(--primary-color);
  color: var(--black-color);
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.search-input::placeholder {
  color: var(--black-color-3);
  opacity: 0.7;
}

.search-input:focus {
  background: var(--primary-color);
  box-shadow: 0 0 0 2px var(--dark-primary-color);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--black-color);
  opacity: 0.7;
}

.clear-button {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--black-color);
  cursor: pointer;
  padding: 0.25rem;
  opacity: 0.7;
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  width: 100%;
  background: var(--primary-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  border: none;
  z-index: 999; /* Ajustado para estar por debajo del filtro móvil */
  overflow: hidden;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--black-color);
  transition: all 0.2s ease;
  cursor: pointer;
}

.result-item:hover,
.result-item.highlighted {
  background: var(--dark-primary-color-2);
}

.result-image {
  width: 45px;
  height: 45px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--black-color-3);
}

.result-details {
  flex: 1;
}

.result-details h6 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--black-color);
}

.result-category {
  display: block;
  font-size: 0.8rem;
  color: var (--black-color-3);
  margin-top: 0.2rem;
}

.result-price {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-weight: 500;
}

.original-price {
  text-decoration: line-through;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.final-price {
  font-weight: 600;
  color: var(--black-color);
}

.discount-price {
  color: var(--dark-secondary-color);
}

.discount-badge {
  background: var(--dark-secondary-color);
  color: var(--white-color);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.line-through {
  text-decoration: line-through;
  color: var(--black-color-3);
  font-size: 0.8rem;
  opacity: 0.7;
}

.more-results {
  padding: 0.75rem 1rem;
  background: var(--primary-color);
  border-top: 1px solid var(--dark-primary-color-2);
}

.view-all-btn {
  width: 100%;
  padding: 0.75rem;
  background: var(--dark-primary-color);
  border: none;
  border-radius: 8px;
  color: var(--white-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background: var(--dark-secondary-color);
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: var(--primary-color);
}

.loading-image {
  width: 45px;
  height: 45px;
  border-radius: 8px;
  background: linear-gradient(90deg, var(--dark-primary-color-2) 25%, var(--dark-primary-color) 50%, var(--dark-primary-color-2) 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.loading-content {
  flex: 1;
}

.loading-line {
  height: 0.9rem;
  background: linear-gradient(90deg, var(--dark-primary-color-2) 25%, var(--dark-primary-color) 50%, var(--dark-primary-color-2) 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.loading-line.short {
  width: 60%;
}

@keyframes dropdownFade {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile Search Styles */
.mobile-search {
  position: fixed;
  top: -100%;
  left: 0;
  width: 100%;
  background: var(--black-color-3);
  padding: 1rem;
  transition: top 0.3s ease;
  z-index: 1550; /* Ajustado para estar por debajo del filtro móvil */
}

.mobile-search-active {
  top: 40px;
}

.mobile-search-form {
  display: flex;
  gap: 0.5rem;
}

.mobile-search-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.mobile-search-button,
.mobile-search-close {
  background: none;
  border: none;
  color: var(--white-color);
  padding: 0.5rem;
  font-size: 1.2rem;
}

.mobile-search-btn {
  background: none;
  border: none;
  color: var(--white-color);
  font-size: 1.2rem;
  padding: 0.5rem;
}

/* Mobile styles */
.mobile-logo-section {
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
}

.mobile-logo-section .logo {
  height: 45px;
  width: auto;
}

.mobile-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.mobile-right-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mobile-right-actions .btn {
  padding: 0.5rem;
  color: var(--black-color);
}

.navbar-toggler {
  padding: 0.5rem;
  font-size: 1.25rem;
  border: none;
  color: var(--black-color);
}

.mobile-search-bar {
  padding: 0.5rem 0;
}

/* Mobile container adjustments */
@media (max-width: 991.98px) {
  .navbar > .container {
    flex-direction: column;
    padding: 0 1rem;
  }

  .search-container {
    margin: 0;
    max-width: none;
  }
}

/* Desktop styles */
.desktop-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 1rem 0;
  position: relative;
  z-index: 1;
}

.search-container {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 500px;
  margin: 0;
  z-index: 1001;
}

/* Asegurar que el logo y los botones de acción tengan espacio suficiente */
.navbar-brand {
  margin-right: 0;
  position: relative;
  z-index: 1000;
}

.desktop-content .order-lg-3 {
  position: relative;
  z-index: 1000;
}

@media (max-width: 991.98px) {
  .desktop-content {
    display: none !important;
  }
}

/* Ajustar z-indices para mobile */
@media (max-width: 991.98px) {
  .main-header {
    z-index: 999;
  }
  
  .search-container {
    position: relative;
    z-index: 1050;
  }
}

/* New styles for nav-action-btn and cart-badge */
.nav-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  color: var(--black-color);
  text-decoration: none;
  position: relative;
  transition: all 0.2s ease;
}

.nav-action-btn:hover {
  color: var(--secondary-color);
  transform: translateY(-2px);
}

.nav-action-btn.mobile {
  flex-direction: row;
  padding: 0.5rem;
}

.nav-action-btn span {
  font-size: 0.8rem;
  font-weight: 500;
}

.cart-btn {
  position: relative;
}

.cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: var(--primary-color);
  border: 1px solid var(--secondary-color);
  color: var(--black-color);
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.20rem 0.5rem;
  border-radius: 1rem;
  min-width: 1.5rem;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  animation: badge-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.cart-badge.mobile {
  top: -8px;
  right: -8px;
}

@keyframes badge-pop {
  0% {
    transform: scale(0);
  }
  80% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* Adjust mobile styles */
@media (max-width: 991.98px) {
  .mobile-actions-bar {
    padding: 0.75rem 0;
  }

  .mobile-right-actions {
    gap: 1.5rem;
  }

  .nav-action-btn.mobile i {
    font-size: 1.25rem;
  }
}

/* Add styles for the no results search button */
.no-results-search-button {
  padding: 0.75rem;
  border-bottom: 1px solid var(--dark-primary-color-2);
}

.search-all-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--dark-secondary-color-2);
  border: none;
  border-radius: 8px;
  color: var(--white-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  text-align: left;
  transition: all 0.2s ease;
}

.search-all-btn:hover {
  background: var(--dark-secondary-color);
}

.search-all-btn span {
  margin-right: auto;
}
</style>